import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "chartjs-adapter-date-fns";

import {
  calculateProductValues,
  fetchProductDynamic,
  getCategories,
} from "../../services/productServices";

Chart.register(...registerables, ChartDataLabels);

function ValueTrend() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    category: "Coffee / ቡና",
    productId: "1",
  });

  // --- NEW: currency selector + exchange rate ---
  const [currency, setCurrency] = useState("ETB");
  const [exchangeRate, setExchangeRate] = useState(1);

  const fetchExchangeRate = async (target) => {
    if (target === "ETB") {
      setExchangeRate(1);
      return;
    }
    try {
      const res = await fetch(
        `https://currency-conversion-and-exchange-rates.p.rapidapi.com/latest?base=ETB&symbols=${target}`,
        {
          method: "GET",
          headers: {
            "x-rapidapi-key":
              "f7dba3c1d5msh3029e3b69d63aaep10f347jsn79ce51586bb1",
            "x-rapidapi-host":
              "currency-conversion-and-exchange-rates.p.rapidapi.com",
          },
        }
      );
      const data = await res.json();
      setExchangeRate(data.rates?.[target] ?? 1);
    } catch (e) {
      console.error("Failed to fetch exchange rate", e);
      setExchangeRate(1);
    }
  };

  // fetch rate whenever user switches currency
  useEffect(() => {
    fetchExchangeRate(currency);
  }, [currency]);

  // --- existing fetchCategories, fetchProducts, fetchValues logic ---
  useEffect(() => {
    getCategories()
      .then((cats) => {
        const list = Array.isArray(cats) ? cats : [];
        setCategories(list);
        if (!list.find((c) => c.name === filters.category) && list.length) {
          setFilters((f) => ({ ...f, category: list[0].name }));
        }
      })
      .catch((e) => console.error("fetchCategories", e));
  }, []);

  useEffect(() => {
    if (!filters.category) {
      setProducts([]);
      return;
    }

    fetchProductDynamic(filters.category)
      .then((prods) => {
        const list = Array.isArray(prods) ? prods : [];
        setProducts(list);
        // Reset to first product in the new category (or clear if empty)
        if (list.length > 0) {
          setFilters((f) => ({
            ...f,
            productId: String(list[0].id),
          }));
        } else {
          setFilters((f) => ({ ...f, productId: "" }));
        }
      })
      .catch((e) => {
        console.error("fetchProducts", e);
        setProducts([]);
        setFilters((f) => ({ ...f, productId: "" }));
      });
  }, [filters.category]);

  useEffect(() => {
    if (!filters.category || !filters.productId) return;
    setLoading(true);
    setError("");
    calculateProductValues({
      category: filters.category,
      productId: parseInt(filters.productId, 10),
      monthlyTrend: true,
    })
      .then((res) => {
        if (Array.isArray(res)) setValues(res);
        else {
          setValues([]);
          setError(res.message || "No data returned");
        }
      })
      .catch((e) => {
        console.error("calculateProductValues", e);
        setError("Failed to load values");
      })
      .finally(() => setLoading(false));
  }, [filters.category, filters.productId]);

  const hasData = values.length > 0 && products.length > 0;

  const formatLargeNumber = (val) => {
    if (val >= 1_000_000_000) {
      return `${(Math.round(val / 100_000_000) / 10).toFixed(1)}B`;
    } else if (val >= 1_000_000) {
      return `${(Math.round(val / 100_000) / 10).toFixed(1)}M`;
    } else if (val >= 1_000) {
      return `${(Math.round(val / 100) / 10).toFixed(1)}K`;
    } else {
      return val.toLocaleString();
    }
  };

  // build chart data with conversion
  const chartData = React.useMemo(() => {
    if (!hasData) return {};

    const labels = values.map((v) => new Date(v.month + "-01"));
    const dataPoints = values.map((v) => {
      const entry = Array.isArray(v.data) && v.data[0];
      const etbVal = entry ? entry.estimated_total_value : 0;
      return etbVal * exchangeRate;
    });

    return {
      labels,
      datasets: [
        {
          label: `${currency}`,
          data: dataPoints,
          fill: false,
          borderColor: "blue",
          tension: 0.3,
          pointBackgroundColor: "blue",
          datalabels: {
            anchor: "end",
            align: "top",
            offset: 8,
            clip: false,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            borderRadius: 4,
            padding: 4,
            font: { size: 10, weight: "bold" },
            color: "#333",
            formatter: (val) => formatLargeNumber(val),
          },
        },
      ],
    };
  }, [values, products, filters.productId, exchangeRate, currency, hasData]);

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: { unit: "month", tooltipFormat: "MMM yyyy" },
        ticks: { autoSkip: true, maxRotation: 0 },
      },
      y: {
        beginAtZero: false,
        ticks: {
          callback: (val) =>
            val.toLocaleString(undefined, {
              style: "currency",
              currency,
              maximumFractionDigits: 2,
            }),
        },
      },
    },
    plugins: { datalabels: { display: true }, legend: { display: true } },
  };

  // get current product name
  const selectedProductName =
    products.find((p) => String(p.id) === filters.productId)?.name ?? "-";

  return (
    <div className="p-4 bg-white shadow-lg">
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Category dropdown */}
        <div>
          <label className="block mb-1 font-medium">Category</label>
          <select
            className="border rounded p-2"
            value={filters.category}
            onChange={(e) =>
              setFilters((f) => ({ ...f, category: e.target.value }))
            }
          >
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Product dropdown */}
        <div>
          <label className="block mb-1 font-medium">Product</label>
          <select
            className="border rounded p-2"
            value={filters.productId}
            onChange={(e) =>
              setFilters((f) => ({ ...f, productId: e.target.value }))
            }
          >
            {products.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* NEW: Currency dropdown */}
        <div>
          <label className="block mb-1 font-medium">Currency</label>
          <select
            className="border rounded p-2"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="ETB">ETB</option>
            <option value="USD">USD</option>
            {/* <option value="EUR">EUR</option>
            <option value="GBP">GBP</option> */}
          </select>
        </div>
      </div>

      {/* Status */}
      {loading && <p>Loading chart…</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Title */}
      {!loading && !error && hasData && (
        <div className="mb-2 text-lg font-bold text-gray-700">
          {selectedProductName} ({currency})
        </div>
      )}

      {/* Chart */}
      {!loading && !error && hasData && (
        <div style={{ height: 400 }}>
          <Line data={chartData} options={chartOpts} />
        </div>
      )}
    </div>
  );
}

export default ValueTrend;
